import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

interface UserContextType {
  user: {
    firstName: string;
    acknowledged: boolean;
  };
  setFirstName: (name: string) => void;
  setAcknowledged: (value: boolean) => void;
}

const defaultContext: UserContextType = {
  user: {
    firstName: '',
    acknowledged: false,
  },
  setFirstName: () => {},
  setAcknowledged: () => {},
};

export const UserContext = createContext<UserContextType>(defaultContext);

export const useUser = () => useContext(UserContext);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  // Initialize state from localStorage if available
  const [firstName, setFirstName] = useState(() => {
    return localStorage.getItem('jimi_firstName') || '';
  });
  
  const [acknowledged, setAcknowledged] = useState(() => {
    return localStorage.getItem('jimi_acknowledged') === 'true';
  });
  
  // Update localStorage when state changes
  useEffect(() => {
    if (firstName) {
      localStorage.setItem('jimi_firstName', firstName);
    }
  }, [firstName]);
  
  useEffect(() => {
    localStorage.setItem('jimi_acknowledged', acknowledged ? 'true' : 'false');
  }, [acknowledged]);
  
  // Custom setter for firstName that also updates localStorage
  const handleSetFirstName = (name: string) => {
    console.log('UserContext - setting firstName:', name);
    setFirstName(name);
    localStorage.setItem('jimi_firstName', name);
  };
  
  // Custom setter for acknowledged that also updates localStorage
  const handleSetAcknowledged = (value: boolean) => {
    console.log('UserContext - setting acknowledged:', value);
    setAcknowledged(value);
    localStorage.setItem('jimi_acknowledged', value ? 'true' : 'false');
  };

  // Create user object
  const user = {
    firstName,
    acknowledged,
  };

  return (
    <UserContext.Provider
      value={{
        user,
        setFirstName: handleSetFirstName,
        setAcknowledged: handleSetAcknowledged,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
